import { useCallback, useEffect, useState, type ChangeEvent, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { styled } from '@linaria/react'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import { MinimalAlert, MinimalBadge, MinimalButton, MinimalCard, MinimalCheckbox, MinimalDropdown, MinimalSwitch } from '@ovasabi/ui-minimal'
import { KitchenBadge, ListSkeleton, PageHeader, PageHeaderActionButton, Screen, SectionHeading } from '../../components/chow'
import { CheckIcon } from '../../components/icons'
import { Input } from '../../components/ui/Input'
import {
  commandPayload,
  commands,
  isLiveRuntime,
  uploadVerificationDocument,
  type VerificationDocumentKind,
  type VerificationUploadFields,
} from '../../lib/runtime'
import { useChooseChow, usePageContext } from './ChooseChowContext'
import { kitchenAddress, KitchenLocationRequired, useKitchenCatalog } from './kitchenCatalog'
import { ChefPayoutAccountForm, useChefPayoutAccount } from './ChefPayoutAccount'

/**
 * Chef verification checklist (Chef Verification & Hygiene Standards, layer 1).
 *
 * A kitchen cannot sell until every onboarding step is done and a reviewer
 * approves it. The page renders the server's own checklist (`missing`) and the
 * server's agreement text, so the app never disagrees with what the review
 * actually requires. Every write is awaited and every failure is shown: a
 * verification step that silently "saved" would leave a chef waiting on a
 * review that was never submitted.
 */

type Row = Record<string, unknown>

interface VerificationView {
  vendor: Row
  dossier: Row & { kitchen_photos?: Record<string, string> }
  missing: string[]
  review_pending: boolean
  cert_valid: boolean
  phone_otp_available: boolean
  agreement_version: string
  agreement_clauses: string[]
  kitchen_photo_slots: string[]
}

const toView = (raw: unknown): VerificationView | null => {
  const p = commandPayload(raw)
  if (!p.vendor) return null
  return {
    vendor: (p.vendor ?? {}) as Row,
    dossier: (p.dossier ?? {}) as VerificationView['dossier'],
    missing: Array.isArray(p.missing) ? (p.missing as string[]) : [],
    review_pending: p.review_pending === true,
    cert_valid: p.cert_valid === true,
    phone_otp_available: p.phone_otp_available === true,
    agreement_version: String(p.agreement_version ?? ''),
    agreement_clauses: Array.isArray(p.agreement_clauses) ? (p.agreement_clauses as string[]) : [],
    kitchen_photo_slots: Array.isArray(p.kitchen_photo_slots) ? (p.kitchen_photo_slots as string[]) : [],
  }
}

const SLOT_LABEL: Record<string, string> = {
  cooking_area: 'Cooking area',
  food_storage: 'Food storage',
  water_source: 'Water source',
  waste_disposal: 'Waste disposal',
  fridge_interior: 'Inside the fridge',
}

const STATUS_COPY: Record<string, { tone: 'success' | 'warning' | 'danger' | 'info'; title: string; body: string }> = {
  unverified: {
    tone: 'info',
    title: 'Not verified yet',
    body: 'Finish the steps below and submit. Customers see your kitchen once our team approves it.',
  },
  pending: {
    tone: 'info',
    title: 'In review',
    body: 'Our team is checking your documents and kitchen. Keep building your menu while you wait.',
  },
  verified: {
    tone: 'success',
    title: 'Verified',
    body: 'Your kitchen is live. Customers can report a hygiene concern on any order, so keep your standards up.',
  },
  suspended: {
    tone: 'danger',
    title: 'Paused for a hygiene review',
    body: 'A customer reported a hygiene concern. Sales are paused while our team reviews it, and we will contact you.',
  },
  rejected: {
    tone: 'warning',
    title: 'Changes needed',
    body: 'Read the reviewer notes, fix those items, and submit again.',
  },
}

/** One checklist step, keyed by the server's `missing` entries. */
interface ChecklistStep {
  key: string
  title: string
  done: boolean
}

/**
 * The steps in the order the page shows them. Done-ness comes only from the
 * server's `missing` list, so the progress bar and the Submit button can never
 * disagree with what the review actually requires.
 */
export const checklistSteps = (view: Pick<VerificationView, 'missing' | 'kitchen_photo_slots'>, reverifying: boolean): ChecklistStep[] => {
  const missing = new Set(view.missing)
  const photosDone = view.kitchen_photo_slots.every((slot) => !missing.has(`kitchen_photo:${slot}`))
  const all: ChecklistStep[] = [
    { key: 'id_document', title: 'Government ID', done: !missing.has('id_document') },
    { key: 'selfie', title: 'Selfie', done: !missing.has('selfie') },
    { key: 'phone', title: 'Phone number', done: !missing.has('phone') },
    { key: 'kitchen_photos', title: 'Kitchen photos', done: photosDone },
    { key: 'kitchen_video', title: reverifying ? 'Fresh kitchen video' : 'Kitchen video', done: !missing.has('kitchen_video') },
    { key: 'agreement', title: 'Hygiene standards', done: !missing.has('agreement') },
  ]
  return reverifying ? all.filter((step) => step.key === 'kitchen_video' || step.key === 'agreement') : all
}

const Style = {
  Intro: styled.p`
    margin: 0;
    color: ${minimalVars.color.textSecondary};
    font-size: ${minimalVars.typography.captionSize};
    line-height: ${minimalVars.typography.lineHeightBody};
  `,
  Step: styled.div`
    display: grid;
    gap: ${minimalVars.space.xs};
  `,
  StepHead: styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${minimalVars.space.xs};
  `,
  StepTitle: styled.h3`
    margin: 0;
    font-family: ${minimalVars.typography.displayFamily};
    font-size: ${minimalVars.typography.h2Size};
    font-weight: ${minimalVars.typography.weightSemibold};
    color: ${minimalVars.color.textPrimary};
  `,
  Hint: styled.p`
    margin: 0;
    color: ${minimalVars.color.textTertiary};
    font-size: ${minimalVars.typography.captionSize};
    line-height: 1.45;
  `,
  Row: styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: ${minimalVars.space.xs};
  `,
  FileLabel: styled.label`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 40px;
    padding: 8px 14px;
    border-radius: ${minimalVars.radius.md};
    border: 1px dashed ${minimalVars.color.borderStrong};
    color: ${minimalVars.color.textPrimary};
    font-size: ${minimalVars.typography.captionSize};
    font-weight: ${minimalVars.typography.weightSemibold};
    cursor: pointer;
    & > input {
      position: absolute;
      width: 1px;
      height: 1px;
      opacity: 0;
    }
    &:focus-within {
      outline: 2px solid ${minimalVars.color.borderFocus};
      outline-offset: 2px;
    }
  `,
  Clauses: styled.ol`
    margin: 0;
    padding-left: 1.2rem;
    display: grid;
    gap: 6px;
    color: ${minimalVars.color.textSecondary};
    font-size: ${minimalVars.typography.captionSize};
    line-height: 1.5;
  `,
  Progress: styled.div`
    display: grid;
    gap: 6px;
  `,
  Track: styled.div`
    height: 8px;
    border-radius: ${minimalVars.radius.pill};
    background: ${minimalVars.color.bgSurfaceAlt};
    overflow: hidden;
    > span {
      display: block;
      height: 100%;
      border-radius: inherit;
      background: ${minimalVars.color.brand};
      transition: width 240ms ease;
    }
  `,
  Toggle: styled.label`
    display: flex;
    align-items: center;
    gap: ${minimalVars.space.xs};
    font-size: ${minimalVars.typography.captionSize};
    color: ${minimalVars.color.textPrimary};
  `,
}

const Done = ({ done }: { done: boolean }) =>
  done ? (
    <MinimalBadge tone="success" size="sm">
      <CheckIcon size="sm" /> Done
    </MinimalBadge>
  ) : (
    <MinimalBadge tone="warning" size="sm">To do</MinimalBadge>
  )

const FilePick = ({
  label,
  accept,
  capture,
  disabled,
  onFile,
}: {
  label: string
  accept: string
  capture?: 'user' | 'environment'
  disabled?: boolean
  onFile: (file: File) => void
}) => (
  <Style.FileLabel aria-disabled={disabled}>
    {label}
    <input
      type="file"
      accept={accept}
      capture={capture}
      disabled={disabled}
      onChange={(event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        event.target.value = ''
        if (file) onFile(file)
      }}
    />
  </Style.FileLabel>
)

const StepCard = ({ title, done, children }: { title: string; done: boolean; children: ReactNode }) => (
  <MinimalCard padding="sm" enter={false}>
    <Style.Step>
      <Style.StepHead>
        <Style.StepTitle>{title}</Style.StepTitle>
        <Done done={done} />
      </Style.StepHead>
      {children}
    </Style.Step>
  </MinimalCard>
)

const ActiveChefVerificationPage = () => {
  const navigate = useNavigate()
  const { profileId } = useChooseChow()
  const page = usePageContext('/verification')
  const { account: payoutAccount, reload: reloadPayoutAccount } = useChefPayoutAccount(profileId)
  const [view, setView] = useState<VerificationView | null>(null)
  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [idType, setIdType] = useState<'nin_slip' | 'drivers_licence'>('nin_slip')
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [certIssuer, setCertIssuer] = useState('')
  const [certExpiry, setCertExpiry] = useState('')
  const [agreed, setAgreed] = useState(false)
  const [loadFailed, setLoadFailed] = useState(false)

  const run = useCallback(async (key: string, action: () => Promise<unknown>, success = '') => {
    setBusy(key)
    setError('')
    setNotice('')
    try {
      const next = toView(await action())
      if (next) setView(next)
      if (key === 'load') setLoadFailed(false)
      if (success) setNotice(success)
    } catch (err) {
      if (key === 'load') setLoadFailed(true)
      setError(err instanceof Error ? err.message : 'That did not work. Check your connection and try again.')
    } finally {
      setBusy('')
    }
  }, [])

  useEffect(() => {
    if (isLiveRuntime()) void run('load', () => commands.verification.getMine())
  }, [run])

  const upload = (kind: VerificationDocumentKind, file: File, fields: VerificationUploadFields = {}, key: string = kind) =>
    run(key, () => uploadVerificationDocument(file, kind, fields), 'Uploaded.')

  const missing = new Set(view?.missing ?? [])
  const dossier = view?.dossier ?? {}
  const status = String(view?.vendor.verification_status ?? 'unverified')
  const statusCopy = STATUS_COPY[status] ?? STATUS_COPY.unverified
  const reverifying = Boolean(view?.vendor.first_verified_at)
  const sickPaused = view?.vendor.is_sick_paused === true
  const due = String(view?.vendor.reverification_due ?? '')
  const steps = view ? checklistSteps(view, reverifying) : []
  const doneCount = steps.filter((step) => step.done).length
  const nextStep = steps.find((step) => !step.done)
  const number = (key: string, title: string) => {
    if (reverifying) return title
    const index = steps.findIndex((step) => step.key === key)
    return index >= 0 ? `${index + 1}. ${title}` : title
  }

  return (
    <Screen
      header={(
        <PageHeader
          title={page.title}
          accentDot
          action={(
            <PageHeaderActionButton size="sm" onClick={() => navigate('/dashboard')}>
              Kitchen
            </PageHeaderActionButton>
          )}
        />
      )}
    >
      {!isLiveRuntime() ? (
        <MinimalAlert tone="info" title="Connect to verify">
          Verification needs a live connection to our servers.
        </MinimalAlert>
      ) : null}
      {error && !loadFailed ? <MinimalAlert tone="danger" title="Not saved">{error}</MinimalAlert> : null}
      {notice ? <MinimalAlert tone="success" title="Saved">{notice}</MinimalAlert> : null}

      {view ? (
        <MinimalCard padding="sm" enter={false}>
          <Style.Step>
            <Style.StepHead>
              <Style.StepTitle>{statusCopy.title}</Style.StepTitle>
              <KitchenBadge tier={view.vendor.badge_tier} />
            </Style.StepHead>
            <Style.Intro>{statusCopy.body}</Style.Intro>
            {dossier.review_notes ? <Style.Hint>Reviewer notes: {String(dossier.review_notes)}</Style.Hint> : null}
            {status === 'verified' && due ? (
              <Style.Hint>
                Re-verification due by {new Date(due).toLocaleDateString('en-NG', { dateStyle: 'medium' })}: record a
                fresh kitchen video before then.
              </Style.Hint>
            ) : null}
            {status === 'verified' && !view.cert_valid ? (
              <Style.Hint>Add a food-handler certificate below to earn the Hygiene Certified badge.</Style.Hint>
            ) : null}
            {status !== 'verified' && status !== 'suspended' && steps.length > 0 ? (
              <Style.Progress aria-label="Verification progress">
                <Style.Hint>
                  {doneCount} of {steps.length} steps done
                  {view.review_pending ? ' · submitted' : nextStep ? ` · next: ${nextStep.title}` : ' · ready to submit'}
                </Style.Hint>
                <Style.Track role="progressbar" aria-valuemin={0} aria-valuemax={steps.length} aria-valuenow={doneCount}>
                  <span style={{ width: `${(doneCount / steps.length) * 100}%` }} />
                </Style.Track>
              </Style.Progress>
            ) : null}
          </Style.Step>
        </MinimalCard>
      ) : null}

      {view && status === 'verified' ? (
        <MinimalCard padding="sm" enter={false}>
          <SectionHeading title="Feeling unwell?" />
          <Style.Intro>
            Do not cook while you are ill. Pausing for illness hides your kitchen from new orders and never affects your
            badge, reviews or ranking.
          </Style.Intro>
          <MinimalSwitch checked={sickPaused} disabled={busy === 'sick'} label={sickPaused ? 'Paused for illness' : 'Pause my kitchen for illness'}
            onCheckedChange={(checked) => void run('sick', () => commands.verification.setSickPause(checked), checked ? 'Kitchen paused. Get well soon.' : 'Welcome back. Your kitchen is taking orders.')} />
        </MinimalCard>
      ) : null}

      {view ? (
        <>
          <SectionHeading title={reverifying ? 'Re-verification' : 'Get verified'} />
          <Style.Intro>
            {reverifying
              ? 'Every 6 months we ask for a fresh kitchen video and your agreement to the current hygiene standards.'
              : 'Only our review team sees these documents. Each step saves as soon as you finish it, so you can stop and come back.'}
          </Style.Intro>

          {!reverifying ? (
            <>
              <StepCard title={number('id_document', 'Government ID')} done={!missing.has('id_document')}>
                <Style.Hint>Upload your NIN slip or driver's licence. Make sure your photo and name are clear.</Style.Hint>
                <Style.Row>
                  <MinimalDropdown
                    label="ID type"
                    value={idType}
                    onChange={setIdType}
                    options={[
                      { value: 'nin_slip', label: 'NIN slip' },
                      { value: 'drivers_licence', label: "Driver's licence" },
                    ]}
                  />
                  <FilePick label={busy === 'id_document' ? 'Uploading…' : 'Upload ID'} accept="image/jpeg,image/png,image/webp,application/pdf"
                    disabled={Boolean(busy)} onFile={(f) => void upload('id_document', f, { idDocumentType: idType })} />
                </Style.Row>
              </StepCard>

              <StepCard title={number('selfie', 'Selfie')} done={!missing.has('selfie')}>
                <Style.Hint>A clear photo of your face, so we can match it to your ID.</Style.Hint>
                <FilePick label={busy === 'selfie' ? 'Uploading…' : 'Take a selfie'} accept="image/jpeg,image/png,image/webp" capture="user"
                  disabled={Boolean(busy)} onFile={(f) => void upload('selfie', f)} />
              </StepCard>

              <StepCard title={number('phone', 'Phone number')} done={!missing.has('phone')}>
                {dossier.phone_verified_at ? (
                  <Style.Hint>Confirmed: {String(dossier.phone_number)}</Style.Hint>
                ) : view.phone_otp_available ? (
                  <>
                    <Style.Row>
                      <Input label="Mobile number" type="tel" placeholder="0803 123 4567" value={phone}
                        onChange={(e) => setPhone(e.target.value)} autoComplete="tel" />
                      <MinimalButton size="sm" variant="secondary" disabled={Boolean(busy) || !phone}
                        onClick={() => void run('otp', () => commands.verification.requestPhoneOtp(phone), 'Code sent by SMS.')}>
                        Send code
                      </MinimalButton>
                    </Style.Row>
                    <Style.Row>
                      <Input label="6-digit code" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)}
                        autoComplete="one-time-code" />
                      <MinimalButton size="sm" disabled={Boolean(busy) || code.length < 6}
                        onClick={() => void run('confirm', () => commands.verification.confirmPhoneOtp(code), 'Phone confirmed.')}>
                        Confirm
                      </MinimalButton>
                    </Style.Row>
                  </>
                ) : (
                  <Style.Hint>
                    Our review team will call the number on your account to confirm it. Make sure it is correct in your
                    profile.
                  </Style.Hint>
                )}
              </StepCard>

              <StepCard title={number('kitchen_photos', 'Kitchen photos')} done={view.kitchen_photo_slots.every((s) => !missing.has(`kitchen_photo:${s}`))}>
                <Style.Hint>One clear photo of each. Use your own kitchen, as it is today.</Style.Hint>
                {view.kitchen_photo_slots.map((slot) => (
                  <Style.Row key={slot}>
                    <Done done={!missing.has(`kitchen_photo:${slot}`)} />
                    <FilePick label={busy === `photo:${slot}` ? 'Uploading…' : SLOT_LABEL[slot] ?? slot}
                      accept="image/jpeg,image/png,image/webp" capture="environment" disabled={Boolean(busy)}
                      onFile={(f) => void upload('kitchen_photo', f, { slot }, `photo:${slot}`)} />
                  </Style.Row>
                ))}
              </StepCard>
            </>
          ) : null}

          <StepCard title={number('kitchen_video', reverifying ? 'Fresh kitchen video' : 'Kitchen video')} done={!missing.has('kitchen_video')}>
            <Style.Hint>
              Walk through your kitchen in about one minute: cooking area, storage, water, waste and fridge. A video
              shows us the kitchen you actually cook in. Keep this page open while it uploads.
            </Style.Hint>
            <FilePick label={busy === 'kitchen_video' ? 'Uploading… keep this page open' : 'Record or upload video'}
              accept="video/mp4,video/quicktime,video/webm" capture="environment" disabled={Boolean(busy)}
              onFile={(f) => void upload('kitchen_video', f)} />
          </StepCard>

          <StepCard title="Bank account for payouts" done={Boolean(payoutAccount?.account_tail)}>
            <Style.Hint>
              Not part of the review, but needed before you are paid. Paystack sends your share of each order to this
              account. Use an account in your own name.
            </Style.Hint>
            <ChefPayoutAccountForm profileId={profileId} compact onSuccess={reloadPayoutAccount} />
          </StepCard>

          <StepCard title="Food-handler certificate (optional)" done={Boolean(dossier.food_handler_cert_key)}>
            <Style.Hint>
              From NAFDAC, your state health office or an accepted online course. It earns the Hygiene Certified badge
              once reviewed.
            </Style.Hint>
            <Style.Row>
              <Input label="Issued by" value={certIssuer} onChange={(e) => setCertIssuer(e.target.value)} />
              <Input label="Expiry date" type="date" value={certExpiry} onChange={(e) => setCertExpiry(e.target.value)} />
            </Style.Row>
            <FilePick label={busy === 'food_handler_cert' ? 'Uploading…' : 'Upload certificate'}
              accept="image/jpeg,image/png,image/webp,application/pdf" disabled={Boolean(busy) || !certIssuer || !certExpiry}
              onFile={(f) => void upload('food_handler_cert', f, { certIssuer, certExpiry })} />
            {dossier.cert_expiry ? (
              <Style.Hint>
                On file, expires {String(dossier.cert_expiry)}{dossier.cert_approved_at ? ' · approved' : ' · awaiting review'}
              </Style.Hint>
            ) : null}
          </StepCard>

          <StepCard title={number('agreement', 'Hygiene standards')} done={!missing.has('agreement')}>
            <Style.Clauses>
              {view.agreement_clauses.map((clause) => <li key={clause}>{clause}</li>)}
            </Style.Clauses>
            {missing.has('agreement') ? (
              <>
                <MinimalCheckbox required checked={agreed} onCheckedChange={setAgreed} label="I have read and agree to these standards." />
                <MinimalButton size="sm" disabled={!agreed || Boolean(busy)}
                  onClick={() => void run('agree', () => commands.verification.acceptAgreement(view.agreement_version), 'Agreement accepted.')}>
                  Accept standards
                </MinimalButton>
              </>
            ) : (
              <Style.Hint>Accepted (version {String(dossier.agreement_version)}).</Style.Hint>
            )}
          </StepCard>

          {status !== 'suspended' ? (
            <MinimalButton
              disabled={Boolean(busy) || missing.size > 0 || view.review_pending}
              loading={busy === 'submit'}
              onClick={() => void run('submit', () => commands.verification.submit(), 'Submitted for review.')}
            >
              {view.review_pending ? 'Waiting for review' : missing.size > 0 ? `${missing.size} step${missing.size === 1 ? '' : 's'} left` : 'Submit for review'}
            </MinimalButton>
          ) : null}
        </>
      ) : loadFailed ? (
        <MinimalAlert tone="danger" title="Could not load your verification"
          action={<MinimalButton size="sm" onClick={() => void run('load', () => commands.verification.getMine())}>Try again</MinimalButton>}>
          {error || 'Check your connection and try again.'}
        </MinimalAlert>
      ) : isLiveRuntime() ? (
        <ListSkeleton rows={4} label="Loading your verification" />
      ) : null}
    </Screen>
  )
}

export const ChefVerificationPage = () => {
  const { profileId } = useChooseChow()
  const catalog = useKitchenCatalog(profileId)
  const navigate = useNavigate()
  if (!catalog.paused && !catalog.loading) return <ActiveChefVerificationPage />
  return <Screen header={<PageHeader title="Kitchen setup" />}>
    <MinimalCard padding="md">
      <MinimalAlert tone="info">Add your kitchen location first. Customers need it to collect orders, and you need it before you can add dishes and menus.</MinimalAlert>
      {catalog.locations.length > 0 ? <>
        <MinimalAlert tone="success" title="Kitchen location ready">{kitchenAddress(catalog.locations[0])}</MinimalAlert>
        <MinimalButton variant="secondary" onClick={() => navigate('/profile#kitchen-locations')}>Update kitchen location</MinimalButton>
      </> : <KitchenLocationRequired loading={catalog.loading} error={catalog.error} reload={catalog.reload} />}
      {catalog.locations.length > 0 && <MinimalButton onClick={() => navigate('/menu')}>Manage dishes and menus</MinimalButton>}
    </MinimalCard>
  </Screen>
}
